// src/services/api.js
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// const API_URL = 'http://164.92.202.73:3000';
// const API_URL = 'http://localhost:3000';
const API_URL = 'https://waslkapi.mahermalhem.site';

const api = axios.create({
  baseURL: API_URL,
});
// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // This will be handled by the AuthContext
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);
// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Trademarks API with React Query hooks
export const useTrademarks = () => {
  return useQuery({
    queryKey: ['trademarks'],
    queryFn: () => api.get('/trademarks').then(res => res.data.data),
  });
};

export const useTrademarkMutations = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/trademarks', data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['trademarks']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/trademarks/${id}`, data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['trademarks']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/trademarks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['trademarks']);
    },
  });

  return { createMutation, updateMutation, deleteMutation };
};

// Categories API with React Query hooks
export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/category').then(res => res.data.data),
  });
};
// Get only leaf categories (categories without children)
export const useLeafCategories = () => {
  return useQuery({
    queryKey: ['categories', 'leaves'],
    queryFn: async () => {
      const response = await api.get('/category');
      const allCategories = response.data.data || [];
      
      const findLeaves = (categories, leaves = []) => {
        if (!categories || !categories.length) return leaves;
        
        categories.forEach(category => {
          if (!category.children || category.children.length === 0) {
            leaves.push(category);
          } else {
            findLeaves(category.children, leaves);
          }
        });
        
        return leaves;
      };
      
      return findLeaves(allCategories);
    },
  });
};


// Product API with React Query hooks
export const useProducts = (params = {}) => {
  return useQuery({
    queryKey: ['products', params],
    queryFn: async () => {
      const response = await api.get('/product', { params });
      return {
        products: response.data.data.products,
        meta: response.data.data.meta
      };
    },
  });
};

export const useProduct = (id) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const response = await api.get(`/product/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
};


export const useProductMutations = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data) => 
      api.post('/product', data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => 
      api.patch(`/product/${id}`, data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      queryClient.invalidateQueries('product');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => 
      api.delete(`/product/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
    },
  });

  return { createMutation, updateMutation, deleteMutation };
};

export const useCategoryMutations = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/category', data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/category/${id}`, data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/category/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
    },
  });

  return { createMutation, updateMutation, deleteMutation };
};

// Amman Areas API
export const useAmmanAreas = () => {
  return useQuery({
    queryKey: ['amman-areas'],
    queryFn: () => api.get('/amman-areas').then(res => res.data.data),
  });
};

export const useAmmanAreaMutations = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/amman-areas', data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['amman-areas']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/amman-areas/${id}`, data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['amman-areas']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/amman-areas/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['amman-areas']);
    },
  });

  return { createMutation, updateMutation, deleteMutation };
};

// Sales API
export const useSales = () => {
  return useQuery({
    queryKey: ['sales'],
    queryFn: () => api.get('/sales').then(res => res.data.data),
  });
};

export const useCreateSale = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => api.post('/sales', data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['sales']);
      queryClient.invalidateQueries(['shops']);
    },
  });
};

export const useUpdateSale = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/sales/${id}`, data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['sales']);
      queryClient.invalidateQueries(['shops']);
    },
  });
};

export const useDeleteSale = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id) => api.delete(`/sales/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['sales']);
      queryClient.invalidateQueries(['shops']);
    },
  });
};

// Return invoice API
const parseReturnInvoicesResponse = (response) => {
  const data = response.data?.data ?? response.data;
  const invoices =
    data?.returnInvoices ??
    data?.invoices ??
    data?.items ??
    (Array.isArray(data) ? data : []);
  const meta = data?.meta ?? response.data?.meta ?? {
    total: invoices.length,
    page: 1,
    per_page: 10,
  };
  return { invoices, meta };
};

export const useReturnInvoices = (params = {}) => {
  return useQuery({
    queryKey: ['return-invoices', params],
    queryFn: async () => {
      const response = await api.get('/return-invoice', { params });
      return parseReturnInvoicesResponse(response);
    },
  });
};

export const useReturnInvoice = (id) => {
  return useQuery({
    queryKey: ['return-invoice', id],
    queryFn: async () => {
      const response = await api.get(`/return-invoice/${id}`);
      return response.data?.data ?? response.data;
    },
    enabled: !!id,
  });
};

export const useCreateReturnInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => api.post('/return-invoice', data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['return-invoices']);
    },
  });
};

export const useUpdateReturnInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }) =>
      api.patch(`/return-invoice/${id}`, data).then((res) => res.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['return-invoices']);
      queryClient.invalidateQueries(['return-invoice', variables.id]);
    },
  });
};

export const useUpdateReturnInvoiceItemStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, ...data }) =>
      api.patch(`/return-invoice/item/${itemId}/return-status`, data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['return-invoices']);
    },
  });
};

export const useDeleteReturnInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => api.delete(`/return-invoice/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['return-invoices']);
    },
  });
};

// Shops API
export const useShops = () => {
  return useQuery({
    queryKey: ['shops'],
    queryFn: () => api.get('/shops').then(res => res.data.data),
  });
};

export const useShopMutations = () => {
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: (data) => api.post('/shops', data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['shops']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/shops/${id}`, data).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['shops']);
    },
    onError: (error) => {
      console.error('Failed to update shop:', error);
      throw error;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/shops/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['shops']);
    },
  });

  return { createMutation, updateMutation, deleteMutation };
};

// Users API
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/user').then(res => res.data.data),
  });
};

// Swiper API
export const useSwipers = () => {
  return useQuery({
    queryKey: ['swipers'],
    queryFn: () => api.get('/swiper').then(res => res.data.data),
  });
};

export const useSwiperMutations = () => {
  const queryClient = useQueryClient();
  
  const createSwiper = useMutation({
    mutationFn: (swiperData) => 
      api.post('/swiper', swiperData).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swipers'] });
    },
  });

  const updateSwiper = useMutation({
    mutationFn: ({ id, ...swiperData }) => 
      api.put(`/swiper/${id}`, swiperData).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swipers'] });
    },
  });

  const deleteSwiper = useMutation({
    mutationFn: (id) => 
      api.delete(`/swiper/${id}`).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swipers'] });
    },
  });

  return { createSwiper, updateSwiper, deleteSwiper };
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: async ({ token, newPassword, confirmNewPassword }) => {
      const response = await api.patch(`/user/resetPassword/${token}`, {
        newPassword,
        confirmNewPassword
      });
      return response.data;
    },
  });
};

export const useUserMutations = () => {
  const queryClient = useQueryClient();
  
  const createUser = useMutation({
    mutationFn: (userData) => 
      api.post('/auth/register', userData).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });

  const updateUser = useMutation({
    mutationFn: ({ id, ...userData }) => 
      api.patch(`/user/${id}`, userData).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
    },
  });

  return { createUser, updateUser };
};

// File upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/files/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// Orders API
export const useOrders = (params = {}, options = {}) => {
  const today = new Date().toISOString().split('T')[0];
  const defaultParams = options.skipDateDefaults
    ? { page: 1, per_page: 10, ...params }
    : {
        page: 1,
        per_page: 10,
        start_date: today,
        end_date: today,
        ...params,
      };

  return useQuery({
    queryKey: ['orders', defaultParams, options.skipDateDefaults],
    queryFn: async () => {
      const response = await api.get('/order', { params: defaultParams });
      return response.data.data;
    },
  });
};

export const fetchReturnInvoiceById = async (id) => {
  const response = await api.get(`/return-invoice/${id}`);
  return response.data?.data ?? response.data;
};

export const useOrderStatusMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ orderIds, isApproved }) => 
      api.patch('/order/change-approval-status', { orderIds, isApproved }).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
    },
    onError: (error) => {
      console.error('Failed to update order status:', error);
      throw error;
    },
  });
};

const unwrapReportData = (response) => response.data?.data ?? response.data;

export const useProfitReport = (params = {}) => {
  return useQuery({
    queryKey: ['dashboard', 'profit', params],
    queryFn: () =>
      api
        .get('/dashboard/reports/profit', { params })
        .then(unwrapReportData),
    enabled: Boolean(params.start_date && params.end_date),
  });
};

export const useSalesReport = (params = {}) => {
  return useQuery({
    queryKey: ['dashboard', 'sales', params],
    queryFn: () =>
      api
        .get('/dashboard/reports/sales', { params })
        .then(unwrapReportData),
    enabled: Boolean(params.start_date && params.end_date),
  });
};

export const useLowStockReport = (threshold = 10) => {
  return useQuery({
    queryKey: ['dashboard', 'low-stock', threshold],
    queryFn: () =>
      api
        .get('/dashboard/reports/low-stock', { params: { threshold } })
        .then(unwrapReportData),
  });
};

export const useInactiveUsersReport = (days = 30) => {
  return useQuery({
    queryKey: ['dashboard', 'inactive-users', days],
    queryFn: () =>
      api
        .get('/dashboard/reports/inactive-users', { params: { days } })
        .then(unwrapReportData),
  });
};

// Vacations API
const unwrapVacationData = (response) => response.data?.data ?? response.data;

const normalizeVacationList = (data) => {
  if (Array.isArray(data)) return data;
  return data?.vacations ?? data?.items ?? [];
};

export const useVacations = () => {
  return useQuery({
    queryKey: ['vacations'],
    queryFn: () =>
      api.get('/vacations').then(unwrapVacationData).then(normalizeVacationList),
  });
};

export const useVacationMutations = () => {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/vacations', data).then(unwrapVacationData),
    onSuccess: () => {
      queryClient.invalidateQueries(['vacations']);
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: (data) => api.post('/vacations/bulk', data).then(unwrapVacationData),
    onSuccess: () => {
      queryClient.invalidateQueries(['vacations']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) =>
      api.patch(`/vacations/${id}`, data).then(unwrapVacationData),
    onSuccess: () => {
      queryClient.invalidateQueries(['vacations']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/vacations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['vacations']);
    },
  });

  return { createMutation, bulkCreateMutation, updateMutation, deleteMutation };
};

// Export the api instance in case it's needed elsewhere
export { api, API_URL };
